import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemWithDescription,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  UserPlus,
  Mail,
  Clock,
  MoreVertical,
  Shield,
  Eye,
  CreditCard,
  Crown,
  Megaphone,
  RefreshCw,
  X,
  Trash2,
  Settings,
  AlertCircle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Role configuration
const ROLE_CONFIG = {
  OWNER: {
    label: "Owner",
    description: "Full account control, can transfer ownership",
    icon: Crown,
    color: "bg-amber-100 text-amber-800 border-amber-200",
  },
  ADMIN: {
    label: "Administrator",
    description: "Full access to campaigns, creatives, and settings. Can invite team members.",
    icon: Shield,
    color: "bg-blue-100 text-blue-800 border-blue-200",
  },
  FINANCE: {
    label: "Finance",
    description: "View and export billing, invoices, and spend reports. Manage payment methods.",
    icon: CreditCard,
    color: "bg-green-100 text-green-800 border-green-200",
  },
  MARKETING: {
    label: "Marketing",
    description: "Create and manage campaigns, creatives, and audiences, and view analytics. No billing access.",
    icon: Megaphone,
    color: "bg-purple-100 text-purple-800 border-purple-200",
  },
  READONLY_FINANCE: {
    label: "Read Only (Finance)",
    description: "View all data including campaigns, analytics, and billing. No edit permissions.",
    icon: Eye,
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
  },
  READONLY: {
    label: "Read Only",
    description: "View campaigns, creatives, and analytics. No billing access and no edit permissions.",
    icon: Eye,
    color: "bg-gray-100 text-gray-800 border-gray-200",
  },
};

const RoleBadge = ({ role }) => {
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.READONLY;
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`${config.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

export default function Team() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [isPlatformUser, setIsPlatformUser] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  
  // Invite modal state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("READONLY");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  
  // Role change modal state
  const [roleChangeOpen, setRoleChangeOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers(response.data.members || []);
      setPending(response.data.pending || []);
      setCanManage(response.data.can_manage || false);
      setIsPlatformUser(response.data.is_platform_user || false);
    } catch (error) {
      console.error("Error fetching team:", error);
      toast.error("Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/team/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableRoles(response.data.roles || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchTeam();
      fetchRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleInvite = async () => {
    if (!inviteEmail) {
      toast.error("Please enter an email address");
      return;
    }
    
    setInviteLoading(true);
    try {
      await axios.post(
        `${API}/team/invite`,
        {
          email: inviteEmail,
          role: inviteRole,
          message: inviteMessage || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("READONLY");
      setInviteMessage("");
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvite = async (invitationId) => {
    try {
      await axios.post(
        `${API}/team/invite/${invitationId}/resend`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Invitation resent");
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invitation");
    }
  };

  const handleCancelInvite = async (invitationId) => {
    try {
      await axios.delete(`${API}/team/invite/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Invitation cancelled");
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel invitation");
    }
  };

  const handleRoleChange = async () => {
    if (!selectedMember || !newRole) return;
    
    setRoleChangeLoading(true);
    try {
      await axios.put(
        `${API}/team/${selectedMember.id}/role`,
        { role: newRole },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(`Role updated for ${selectedMember.name}`);
      setRoleChangeOpen(false);
      setSelectedMember(null);
      setNewRole("");
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setRoleChangeLoading(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    try {
      await axios.delete(`${API}/team/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${memberName} has been removed from the team`);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove team member");
    }
  };

  const openRoleChange = (member) => {
    setSelectedMember(member);
    setNewRole(member.role);
    setRoleChangeOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getDaysUntilExpiry = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get current user's role
  const currentUserRole = members.find(m => m.is_current_user)?.role;
  const isOwner = currentUserRole === "OWNER";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isPlatformUser) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Platform User</h3>
            <p className="text-gray-500 text-center mt-2">
              As a Booma platform user, you don't have an advertiser team.
              <br />
              You can manage advertiser accounts from the Admin panel.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-6 w-6" />
            Team Members
          </h1>
          <p className="text-gray-500 mt-1">
            Manage who has access to your account
          </p>
        </div>
        {canManage && (
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Invite Member
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Invite Team Member</DialogTitle>
                <DialogDescription>
                  Send an invitation to join your team. They'll receive an email with a link to create their account.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoles.map((role) => (
                        <SelectItemWithDescription
                          key={role.value}
                          value={role.value}
                          text={role.display}
                          description={role.description}
                        />
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Personal Message (optional)</Label>
                  <Textarea
                    spellCheck="true"
                    id="message"
                    placeholder="Add a personal note to the invitation..."
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleInvite} disabled={inviteLoading}>
                  {inviteLoading ? "Sending..." : "Send Invitation"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Team Members List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Active Members</CardTitle>
          <CardDescription>
            {members.length} team member{members.length !== 1 ? "s" : ""} with access to this account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {members.map((member) => (
              <div
                key={member.id}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  member.is_current_user ? "bg-blue-50 border-blue-200" : "bg-white"
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">
                      {member.name?.charAt(0)?.toUpperCase() || "?"}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{member.name}</span>
                      {member.is_current_user && (
                        <Badge variant="secondary" className="text-xs">You</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{member.email}</p>
                    {member.last_login && (
                      <p className="text-xs text-gray-400">
                        Last login: {formatDate(member.last_login)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <RoleBadge role={member.role} />
                  {canManage && !member.is_current_user && member.role !== "OWNER" && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isOwner && (
                          <DropdownMenuItem onClick={() => openRoleChange(member)}>
                            <Settings className="h-4 w-4 mr-2" />
                            Change Role
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem
                              className="text-red-600"
                              onSelect={(e) => e.preventDefault()}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to remove {member.name} from the team? They will lose all access to this account immediately.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-600 hover:bg-red-700"
                                onClick={() => handleRemoveMember(member.id, member.name)}
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {canManage && pending.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Pending Invitations
            </CardTitle>
            <CardDescription>
              Invitations waiting to be accepted
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pending.map((invitation) => {
                const daysLeft = getDaysUntilExpiry(invitation.expires_at);
                const isExpiringSoon = daysLeft <= 2;
                
                return (
                  <div
                    key={invitation.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-amber-50 border-amber-200"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Mail className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{invitation.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <RoleBadge role={invitation.role} />
                          <span className={`text-xs flex items-center gap-1 ${isExpiringSoon ? "text-red-600" : "text-gray-500"}`}>
                            <Clock className="h-3 w-3" />
                            {daysLeft > 0 ? `Expires in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}` : "Expired"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResendInvite(invitation.id)}
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Resend
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleCancelInvite(invitation.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Role Permissions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Role Permissions</CardTitle>
          <CardDescription>
            What each role can do in your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(ROLE_CONFIG).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <div key={key} className="p-4 border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-5 w-5 text-gray-600" />
                    <span className="font-medium">{config.label}</span>
                  </div>
                  <p className="text-sm text-gray-500">{config.description}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Role Change Dialog */}
      <Dialog open={roleChangeOpen} onOpenChange={setRoleChangeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Update the role for {selectedMember?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>New Role</Label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.display}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {newRole && ROLE_CONFIG[newRole] && (
              <p className="text-sm text-gray-500 mt-2">
                {ROLE_CONFIG[newRole].description}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleChangeOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRoleChange} disabled={roleChangeLoading}>
              {roleChangeLoading ? "Updating..." : "Update Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
